import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Experiments from "@/pages/Experiments";
import ExperimentDetail from "@/pages/ExperimentDetail";
import ExperimentForm from "@/pages/ExperimentForm";
import Projects from "@/pages/Projects";
import CalendarPage from "@/pages/CalendarPage";
import Notes from "@/pages/Notes";
import Settings from "@/pages/Settings";
import Labs from "@/pages/Labs";
import LabDetail from "@/pages/LabDetail";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import RefundPolicy from "@/pages/RefundPolicy";
import Admin from "@/pages/Admin";
import AppLayout from "@/components/AppLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import { usePageView } from "@/lib/analytics";

function Shell({ children }) {
  return (
    <ProtectedRoute>
      <AppLayout>{children}</AppLayout>
    </ProtectedRoute>
  );
}

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();
  usePageView();
  // Detect OAuth callback synchronously during render (not in useEffect)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/dashboard" element={<Shell><Dashboard /></Shell>} />
      <Route path="/experiments" element={<Shell><Experiments /></Shell>} />
      <Route path="/experiments/new" element={<Shell><ExperimentForm /></Shell>} />
      <Route path="/experiments/:id" element={<Shell><ExperimentDetail /></Shell>} />
      <Route path="/experiments/:id/edit" element={<Shell><ExperimentForm /></Shell>} />
      <Route path="/projects" element={<Shell><Projects /></Shell>} />
      <Route path="/calendar" element={<Shell><CalendarPage /></Shell>} />
      <Route path="/notes" element={<Shell><Notes /></Shell>} />
      <Route path="/labs" element={<Shell><Labs /></Shell>} />
      <Route path="/labs/:id" element={<Shell><LabDetail /></Shell>} />
      <Route path="/settings" element={<Shell><Settings /></Shell>} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="labtracker-theme">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster richColors closeButton position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
